import { useCallback, useEffect, useRef, useState } from "react";
import { apiClient } from "../../utils/http";
import { todayISO } from "../../utils/format";

const EMPTY = { items: null, page: 0, hasMore: false, total: 0, loading: false };

/*
 * The paginated upcoming list (GET schedule/feed): page 1 loads whenever
 * `type` or `version` changes, loadMore() appends the next page. A response
 * that arrives after the list was reset is dropped.
 */
export default function useScheduleFeed({ type = null, version, showToast }) {
  const [feed, setFeed] = useState(() => ({ ...EMPTY, type }));
  const generation = useRef(0);

  const fetchPage = useCallback(
    (page) => {
      const gen = generation.current;
      const params = { page, today: todayISO() };
      if (type) params.type = type;
      setFeed((current) => ({ ...current, loading: true }));
      apiClient
        .get("schedule/feed", { params })
        .then((res) => {
          if (gen !== generation.current) return;
          setFeed((current) => {
            const previous = page === 1 || !current.items ? [] : current.items;
            const seen = new Set(previous.map((item) => item.key));
            return {
              items: [...previous, ...res.data.items.filter((item) => !seen.has(item.key))],
              page,
              hasMore: res.data.has_more,
              total: res.data.total,
              loading: false,
            };
          });
        })
        .catch((err) => {
          if (gen !== generation.current) return;
          setFeed((current) => ({ ...current, items: current.items || [], hasMore: false, loading: false }));
          showToast(err.message, "error");
        });
    },
    [type, showToast]
  );

  useEffect(() => {
    generation.current += 1;
    // a type switch starts from the spinner; a refresh keeps the old rows until page 1 is back
    setFeed((current) => (current.type === type ? current : { ...EMPTY, type }));
    fetchPage(1);
  }, [type, version, fetchPage]);

  const loadMore = useCallback(() => {
    if (feed.loading || !feed.hasMore) return;
    fetchPage(feed.page + 1);
  }, [feed.loading, feed.hasMore, feed.page, fetchPage]);

  return { ...feed, loadMore };
}
